import { Link } from 'react-router-dom'
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Layout } from '../components/layout/Layout'
import { useImages, useRefreshExif } from '../hooks/useImages'
import { useAlbums } from '../hooks/useAlbums'
import { adminApiClient } from '../api/client'
import { getThumbnailUrl } from '../lib/images'
import {
  Upload,
  FolderHeart,
  Image,
  TrendingUp,
  Eye,
  EyeOff,
  Search,
  ArrowRight,
  RefreshCw,
  AlertTriangle,
} from 'lucide-react'

interface ImageSummary {
  total: number
  public: number
  discoverable: number
  private: number
  orphaned: number
}

export const Admin = () => {
  const { data: imagesData } = useImages(adminApiClient, { limit: 5 })
  const { data: albums } = useAlbums(adminApiClient)
  const refreshExif = useRefreshExif(adminApiClient)
  const [refreshingId, setRefreshingId] = useState<string | null>(null)

  // Photo counts derive from each photo's parent album visibility. Served by
  // the admin summary endpoint so the dashboard doesn't have to join albums
  // client-side.
  const { data: summary } = useQuery<ImageSummary>({
    queryKey: ['admin', 'summary'],
    queryFn: async () => {
      const { data } = await adminApiClient.get('/summary')
      return data.data as ImageSummary
    },
  })

  const recentImages = imagesData?.pages[0]?.items || []
  const totalImages = summary?.total ?? 0
  const totalAlbums = albums?.length || 0
  const publicImages = summary?.public ?? 0
  const discoverableImages = summary?.discoverable ?? 0
  const privateImages = summary?.private ?? 0

  const stats = [
    {
      label: 'Total Photos',
      value: totalImages,
      icon: Image,
      color: 'bg-blue-500',
      link: '/',
    },
    {
      label: 'Albums',
      value: totalAlbums,
      icon: FolderHeart,
      color: 'bg-purple-500',
      link: '/admin/albums',
    },
    {
      label: 'Public',
      value: publicImages,
      icon: Eye,
      color: 'bg-green-500',
      link: '/',
    },
    {
      label: 'Discoverable',
      value: discoverableImages,
      icon: Search,
      color: 'bg-amber-500',
      link: '/',
    },
    {
      label: 'Private',
      value: privateImages,
      icon: EyeOff,
      color: 'bg-gray-500',
      link: '/',
    },
  ]

  return (
    <Layout title="Admin Dashboard">
      {summary?.orphaned ? (
        <Link
          to="/admin/orphans"
          className="mb-6 flex items-start gap-3 p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100 transition-colors group"
        >
          <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" />
          <div className="text-sm flex-1">
            <strong>{summary.orphaned} orphaned photo{summary.orphaned === 1 ? '' : 's'}</strong> — image record(s)
            whose album no longer exists. <span className="underline decoration-dotted underline-offset-2 group-hover:text-amber-950">Click to manage &rarr;</span>
          </div>
        </Link>
      ) : null}

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        {stats.map((stat) => (
          <Link
            key={stat.label}
            to={stat.link}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 hover:shadow-lg transition-all duration-300 group"
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">{stat.label}</p>
                <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-xl shadow-lg shadow-blue-500/20 group-hover:scale-110 transition-transform`}>
                <stat.icon className="w-6 h-6 text-white" />
              </div>
            </div>
            <div className="mt-4 flex items-center text-sm text-blue-600 font-medium opacity-0 group-hover:opacity-100 transition-opacity">
              View details
              <ArrowRight className="w-4 h-4 ml-1" />
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Quick Actions */}
        <div className="lg:col-span-1">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-4">
            <Link
              to="/admin/upload"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-500 transition-colors">
                <Upload className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Upload Photos</h3>
                <p className="text-sm text-gray-500">Add new images to an album</p>
              </div>
            </Link>

            <Link
              to="/admin/albums"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-500 transition-colors">
                <FolderHeart className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Manage Albums</h3>
                <p className="text-sm text-gray-500">Organize photos into albums</p>
              </div>
            </Link>

            <Link
              to="/"
              className="flex items-center gap-4 p-4 bg-white rounded-xl border border-gray-200 hover:border-green-300 hover:shadow-md transition-all group"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors">
                <TrendingUp className="w-6 h-6 text-green-600 group-hover:text-white transition-colors" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">View Gallery</h3>
                <p className="text-sm text-gray-500">See your public gallery</p>
              </div>
            </Link>

          </div>
        </div>

        {/* Recent Uploads */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Uploads</h2>
          {imagesData === undefined ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-200 rounded-xl animate-pulse" />
                ))}
              </div>
            </div>
          ) : recentImages.length > 0 ? (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 p-4">
                {recentImages.map((image) => (
                  <div key={image.imageId} className="relative group">
                    <Link
                      to={`/image/${image.imageId}`}
                      className="aspect-square rounded-xl overflow-hidden bg-gray-100 hover:opacity-80 transition-opacity block"
                    >
                      <img
                        src={getThumbnailUrl(image, 'small')}
                        alt={image.filename}
                        className="w-full h-full object-cover"
                      />
                    </Link>
                    {/* Refresh EXIF button */}
                    <button
                      onClick={async (e) => {
                        e.preventDefault()
                        setRefreshingId(image.imageId)
                        try {
                          await refreshExif.mutateAsync(image.imageId)
                        } catch (error) {
                          console.error('Failed to refresh EXIF:', error)
                        }
                        setRefreshingId(null)
                      }}
                      disabled={refreshExif.isPending}
                      className="absolute top-2 right-2 p-1.5 bg-black/50 hover:bg-black/70 rounded-lg text-white opacity-0 group-hover:opacity-100 transition-opacity disabled:opacity-50"
                      title="Refresh EXIF"
                    >
                      <RefreshCw className={`w-4 h-4 ${refreshingId === image.imageId ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="px-4 pb-4 text-center">
                <Link
                  to="/"
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                >
                  View all photos →
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-gray-50 rounded-2xl border border-dashed border-gray-200 p-12 text-center">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-gray-900 font-medium mb-1">No photos yet</h3>
              <p className="text-gray-500 mb-4">Upload your first photos to get started</p>
              <Link
                to="/admin/upload"
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Upload className="w-4 h-4" />
                Upload Photos
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  )
}